import React, { useState, useEffect } from 'react';
import { Box, Flex, Image, Stack, Button, Card, Text, TextInput, Tabs } from '@mantine/core';
import theme from 'theme';
import useLang from 'lang';
import { IconInfoCircle } from '@tabler/icons-react';
import H1SemiBold from 'theme/components/Typography/H1SemiBold';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';
import P1Regular from 'theme/components/Typography/P1Regular';
import P2Regular from 'theme/components/Typography/P2Regular';
import P3Bold from 'theme/components/Typography/P3Bold';
import { IconCheckCustom } from 'components/icons/IconCheckCustom';
import { IconXCustom } from 'components/icons/IconXCustom';
import { useApi } from 'api/api-context';

interface SpotPriceData {
  price: number;
  deliveryStart: string;
  deliveryEnd: string;
  period: number;
}

interface AggregatedBarData {
  period: number;
  percentageCheaper: number;
  percentageExpensive: number;
  label: string;
}

type TimeRange = 'day' | 'week' | 'month' | 'year';

const CHART_COLOR_CHEAP = '#50C66E';
const CHART_COLOR_EXPENSIVE = '#E24239';

/** Spot price comparator component */
const SpotComparator: React.FC = () => {
  const { getAction } = useApi();
  const { _t } = useLang();
  const [fixedPrice, setFixedPrice] = useState('0,25');
  const [calculatedPrice, setCalculatedPrice] = useState('0,25');
  const [spotData, setSpotData] = useState<SpotPriceData[]>([]);
  const [loading, setLoading] = useState(false);
  const [calculated, setCalculated] = useState(false);
  const [savings, setSavings] = useState(0);
  const [isSpotBetter, setIsSpotBetter] = useState(false);
  const [activeTab, setActiveTab] = useState<TimeRange>('day');

  /** Fetch spot prices from API */
  const fetchSpotPrices = async (timeRange: TimeRange = 'day') => {
    setLoading(true);
    try {
      const now = new Date();
      let from = new Date();
      let to = new Date();

      switch (timeRange) {
        case 'day':
          from.setHours(0, 0, 0, 0);
          to = new Date(from);
          to.setDate(to.getDate() + 1);
          break;
        case 'week':
          from.setDate(now.getDate() - 7);
          from.setHours(0, 0, 0, 0);
          to = new Date(from);
          to.setDate(to.getDate() + 7);
          to.setHours(0, 0, 0, 0);
          break;
        case 'month':
          from.setDate(now.getDate() - 28);
          from.setHours(0, 0, 0, 0);
          to = new Date(from);
          to.setDate(to.getDate() + 28);
          to.setHours(0, 0, 0, 0);
          break;
        case 'year':
          from = new Date(now.getFullYear() - 1, now.getMonth(), 1, 0, 0, 0, 0);
          to = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
          break;
      }

      const listPrices = getAction('ListElectricitySpotPrices');
      let allData: SpotPriceData[] = [];

      if (timeRange === 'year') {
        // console.log('📊 Rok - načítavam dáta po mesiacoch...');
        const monthlyPromises: Promise<any>[] = [];

        for (let i = 0; i < 12; i++) {
          const monthStart = new Date(from);
          monthStart.setMonth(from.getMonth() + i);
          const monthEnd = new Date(monthStart);
          monthEnd.setMonth(monthEnd.getMonth() + 1);

          monthlyPromises.push(
            listPrices({
              query: {
                from: monthStart.toISOString(),
                to: monthEnd.toISOString(),
                country: 'SK',
              },
            })
          );
        }

        const monthlyResponses = await Promise.all(monthlyPromises);
        monthlyResponses.forEach((response) => {
          const monthData: SpotPriceData[] = (response.spotPrices || []).map((item: any) => ({
            price: item.price || 0,
            deliveryStart: item.start || '',
            deliveryEnd: item.end || '',
            period: 0,
          }));
          allData = [...allData, ...monthData];
        });

        // console.log('✅ Rok - načítané dáta:', allData.length, 'záznamov');
      } else {
        // console.log('📊 API Request:', {
        //   timeRange,
        //   from: from.toISOString(),
        //   to: to.toISOString(),
        // });

        const response = await listPrices({
          query: { from: from.toISOString(), to: to.toISOString(), country: 'SK' },
        });

        allData = (response.spotPrices || []).map((item, index) => ({
          price: item.price || 0,
          deliveryStart: item.start || '',
          deliveryEnd: item.end || '',
          period: index + 1,
        }));

        // console.log('✅ Dáta načítané z API:', allData.length, 'záznamov');
      }

      setSpotData(allData);
    } catch (error) {
      // console.error('❌ Chyba pri načítavaní spotových cien:', error);
      setSpotData([]);
    } finally {
      setLoading(false);
    }
  };

  /** Calculate comparison between spot and fixed prices */
  const calculateComparison = async () => {
    if (spotData.length === 0) return;

    const fixedPriceValue = parseFloat(fixedPrice.replace(',', '.'));

    if (isNaN(fixedPriceValue)) return;

    setCalculatedPrice(fixedPrice);
    setLoading(true);

    try {
      const fixedPriceMWh = fixedPriceValue * 1000;

      const now = new Date();
      let from = new Date();
      let to = new Date();

      switch (activeTab) {
        case 'day':
          from.setHours(0, 0, 0, 0);
          to = new Date(from);
          to.setDate(to.getDate() + 1);
          break;
        case 'week':
          from.setDate(now.getDate() - 7);
          from.setHours(0, 0, 0, 0);
          to = new Date(from);
          to.setDate(to.getDate() + 7);
          to.setHours(0, 0, 0, 0);
          break;
        case 'month':
          from.setDate(now.getDate() - 28);
          from.setHours(0, 0, 0, 0);
          to = new Date(from);
          to.setDate(to.getDate() + 28);
          to.setHours(0, 0, 0, 0);
          break;
        case 'year':
          from = new Date(now.getFullYear() - 1, now.getMonth(), 1, 0, 0, 0, 0);
          to = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
          break;
      }

      // console.log('📊 CompareElectricitySpotPrices Request:', {
      //   tab: activeTab,
      //   from: from.toISOString(),
      //   to: to.toISOString(),
      //   referencePrice: fixedPriceMWh,
      //   referencePriceKWh: fixedPriceValue,
      // });

      const compareAction = getAction('CompareElectricitySpotPrices');
      const response = await compareAction({
        payload: {
          country: 'SK',
          referencePrice: fixedPriceMWh,
          ranges: [
            {
              from: from.toISOString(),
              to: to.toISOString(),
            },
          ],
        },
      });

      // console.log('✅ CompareElectricitySpotPrices Response:', response);

      const result = response.results?.[0];
      if (result) {
        const savingsPercentage = Math.round((result.percentageBelow || 0) * 10) / 10;

        // const negativePrices = spotData.filter((item) => item.price < 0);
        // const zeroPrices = spotData.filter((item) => item.price === 0);

        // // console.log('📈 Výsledok porovnania:', {
        // //   totalIntervals: result.totalIntervals,
        // //   percentageBelow: result.percentageBelow,
        // //   percentageAboveOrEqual: result.percentageAboveOrEqual,
        // //   intervalsBelow: result.intervalsBelow,
        // //   intervalsAboveOrEqual: result.intervalsAboveOrEqual,
        // //   savingsPercentage,
        // //   isSpotBetter: savingsPercentage > 50,
        // // });

        // if (negativePrices.length > 0) {
        //   console.log('⚠️ Záporné spotové ceny:', {
        //     count: negativePrices.length,
        //     percentage: ((negativePrices.length / spotData.length) * 100).toFixed(2) + '%',
        //     samples: negativePrices.slice(0, 5).map((item) => ({
        //       price: item.price,
        //       start: item.deliveryStart,
        //       end: item.deliveryEnd,
        //     })),
        //   });
        // }

        // if (zeroPrices.length > 0) {
        //   console.log('⚠️ Nulové spotové ceny:', {
        //     count: zeroPrices.length,
        //     percentage: ((zeroPrices.length / spotData.length) * 100).toFixed(2) + '%',
        //   });
        // }

        setSavings(savingsPercentage);
        setIsSpotBetter(savingsPercentage > 50);
        setCalculated(true);
      }
    } catch (error) {
      // console.error('❌ Chyba pri porovnávaní cien:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSpotPrices(activeTab);
    setCalculated(false);
  }, [activeTab]);

  useEffect(() => {
    if (spotData.length > 0 && !calculated) {
      calculateComparison();
    }
  }, [spotData]);

  /** Aggregate spot data based on time range */
  const getAggregatedData = (): SpotPriceData[] => {
    if (spotData.length === 0) return [];

    switch (activeTab) {
      case 'day':
        const hourlyData: SpotPriceData[] = [];
        for (let hour = 0; hour < 24; hour++) {
          const hourData = spotData.filter((item) => {
            const date = new Date(item.deliveryStart);
            return date.getHours() === hour;
          });
          if (hourData.length > 0) {
            const avgPrice = hourData.reduce((sum, item) => sum + item.price, 0) / hourData.length;
            hourlyData.push({
              price: avgPrice,
              deliveryStart: hourData[0].deliveryStart,
              deliveryEnd: hourData[hourData.length - 1].deliveryEnd,
              period: hour + 1,
            });
          }
        }
        return hourlyData;

      case 'week':
        const dailyData: SpotPriceData[] = [];
        const dayMap = new Map<string, SpotPriceData[]>();

        spotData.forEach((item) => {
          const date = new Date(item.deliveryStart);
          const dayKey = date.toISOString().split('T')[0];
          if (!dayMap.has(dayKey)) {
            dayMap.set(dayKey, []);
          }
          dayMap.get(dayKey)!.push(item);
        });

        Array.from(dayMap.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .forEach(([_, items], index) => {
            const avgPrice = items.reduce((sum, item) => sum + item.price, 0) / items.length;
            dailyData.push({
              price: avgPrice,
              deliveryStart: items[0].deliveryStart,
              deliveryEnd: items[items.length - 1].deliveryEnd,
              period: index + 1,
            });
          });
        return dailyData;

      case 'month':
        const weeklyData: SpotPriceData[] = [];
        const weekMap = new Map<number, SpotPriceData[]>();

        spotData.forEach((item) => {
          const date = new Date(item.deliveryStart);
          const weekNumber = Math.floor(
            (date.getTime() - new Date(date.getFullYear(), 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000)
          );
          if (!weekMap.has(weekNumber)) {
            weekMap.set(weekNumber, []);
          }
          weekMap.get(weekNumber)!.push(item);
        });

        Array.from(weekMap.entries())
          .sort(([a], [b]) => a - b)
          .slice(-4)
          .forEach(([_, items], index) => {
            const avgPrice = items.reduce((sum, item) => sum + item.price, 0) / items.length;
            weeklyData.push({
              price: avgPrice,
              deliveryStart: items[0].deliveryStart,
              deliveryEnd: items[items.length - 1].deliveryEnd,
              period: index + 1,
            });
          });
        return weeklyData;

      case 'year':
        const monthlyData: SpotPriceData[] = [];
        const monthMap = new Map<string, SpotPriceData[]>();

        spotData.forEach((item) => {
          const date = new Date(item.deliveryStart);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          if (!monthMap.has(monthKey)) {
            monthMap.set(monthKey, []);
          }
          monthMap.get(monthKey)!.push(item);
        });

        Array.from(monthMap.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .forEach(([_, items], index) => {
            const avgPrice = items.reduce((sum, item) => sum + item.price, 0) / items.length;
            monthlyData.push({
              price: avgPrice,
              deliveryStart: items[0].deliveryStart,
              deliveryEnd: items[items.length - 1].deliveryEnd,
              period: index + 1,
            });
          });
        return monthlyData;

      default:
        return spotData;
    }
  };

  /** Get X-axis labels */
  const getXAxisLabels = (): string[] => {
    switch (activeTab) {
      case 'day':
        return Array.from({ length: 24 }, (_, i) => String(i + 1));
      case 'week':
        return [_t('Mon'), _t('Tue'), _t('Wed'), _t('Thu'), _t('Fri'), _t('Sat'), _t('Sun')].slice(
          0,
          getAggregatedData().length
        );
      case 'month':
        return Array.from({ length: 4 }, (_, i) => `${_t('Week')} ${i + 1}`);
      case 'year':
        return [
          _t('Jan'),
          _t('Feb'),
          _t('Mar'),
          _t('Apr'),
          _t('May'),
          _t('Jun'),
          _t('Jul'),
          _t('Aug'),
          _t('Sep'),
          _t('Oct'),
          _t('Nov'),
          _t('Dec'),
        ];
      default:
        return [];
    }
  };

  /** Get chart title */
  const getChartTitle = (): string => {
    switch (activeTab) {
      case 'day':
        return '24 hours';
      case 'week':
        return '7 days';
      case 'month':
        return '4 weeks';
      case 'year':
        return '12 months';
      default:
        return '';
    }
  };

  /** Get bar chart data with percentage breakdown */
  const getBarChartData = (): AggregatedBarData[] => {
    if (spotData.length === 0) return [];

    const fixedPriceValue = parseFloat(calculatedPrice.replace(',', '.'));
    if (isNaN(fixedPriceValue)) return [];
    const fixedPriceMWh = fixedPriceValue * 1000;

    const labels = getXAxisLabels();

    switch (activeTab) {
      case 'day':
        const hourlyBars: AggregatedBarData[] = [];
        for (let hour = 0; hour < 24; hour++) {
          const hourData = spotData.filter((item) => {
            const date = new Date(item.deliveryStart);
            return date.getHours() === hour;
          });
          if (hourData.length > 0) {
            const cheaperCount = hourData.filter((item) => item.price < fixedPriceMWh).length;
            const percentageCheaper = (cheaperCount / hourData.length) * 100;
            hourlyBars.push({
              period: hour + 1,
              percentageCheaper,
              percentageExpensive: 100 - percentageCheaper,
              label: labels[hour] || String(hour + 1),
            });
          }
        }
        return hourlyBars;

      case 'week':
        const dailyBars: AggregatedBarData[] = [];
        const dayMap = new Map<string, SpotPriceData[]>();

        spotData.forEach((item) => {
          const date = new Date(item.deliveryStart);
          const dayKey = date.toISOString().split('T')[0];
          if (!dayMap.has(dayKey)) {
            dayMap.set(dayKey, []);
          }
          dayMap.get(dayKey)!.push(item);
        });

        const sortedDays = Array.from(dayMap.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .slice(0, 7); // Take only first 7 days

        sortedDays.forEach(([, items], index) => {
          const cheaperCount = items.filter((item) => item.price < fixedPriceMWh).length;
          const percentageCheaper = (cheaperCount / items.length) * 100;

          // console.log(`📊 Deň ${dayKey}:`, {
          //   totalIntervals: items.length,
          //   cheaperCount,
          //   percentageCheaper: percentageCheaper.toFixed(1) + '%',
          //   fixedPriceMWh,
          //   samplePrices: items.slice(0, 3).map(d => d.price),
          // });

          dailyBars.push({
            period: index + 1,
            percentageCheaper,
            percentageExpensive: 100 - percentageCheaper,
            label: labels[index] || `D${index + 1}`,
          });
        });
        return dailyBars;

      case 'month':
        const weeklyBars: AggregatedBarData[] = [];
        const weekMap = new Map<number, SpotPriceData[]>();

        spotData.forEach((item) => {
          const date = new Date(item.deliveryStart);
          const weekNumber = Math.floor(
            (date.getTime() - new Date(date.getFullYear(), 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000)
          );
          if (!weekMap.has(weekNumber)) {
            weekMap.set(weekNumber, []);
          }
          weekMap.get(weekNumber)!.push(item);
        });

        Array.from(weekMap.entries())
          .sort(([a], [b]) => a - b)
          .slice(-4)
          .forEach(([_, items], index) => {
            const cheaperCount = items.filter((item) => item.price < fixedPriceMWh).length;
            const percentageCheaper = (cheaperCount / items.length) * 100;
            weeklyBars.push({
              period: index + 1,
              percentageCheaper,
              percentageExpensive: 100 - percentageCheaper,
              label: labels[index] || `T${index + 1}`,
            });
          });
        return weeklyBars;

      case 'year':
        const monthlyBars: AggregatedBarData[] = [];
        const monthMap = new Map<string, SpotPriceData[]>();

        spotData.forEach((item) => {
          const date = new Date(item.deliveryStart);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          if (!monthMap.has(monthKey)) {
            monthMap.set(monthKey, []);
          }
          monthMap.get(monthKey)!.push(item);
        });

        // const allMonths = Array.from(monthMap.keys()).sort();
        // console.log('📊 Rok - mesiace v dátach:', allMonths);
        // console.log('📊 Rok - počet záznamov:', spotData.length);
        // console.log('📊 Rok - prvý záznam:', spotData[0]?.deliveryStart);
        // console.log('📊 Rok - posledný záznam:', spotData[spotData.length - 1]?.deliveryStart);

        const sortedMonths = Array.from(monthMap.entries()).sort(([a], [b]) => a.localeCompare(b));

        // console.log('📊 Rok - počet mesiacov:', sortedMonths.length);

        sortedMonths.forEach(([, items], index) => {
          const cheaperCount = items.filter((item) => item.price < fixedPriceMWh).length;
          const percentageCheaper = (cheaperCount / items.length) * 100;
          monthlyBars.push({
            period: index + 1,
            percentageCheaper,
            percentageExpensive: 100 - percentageCheaper,
            label: labels[index] || `M${index + 1}`,
          });
        });
        return monthlyBars;

      default:
        return [];
    }
  };

  const aggregatedData = getAggregatedData();
  const xAxisLabels = getXAxisLabels();
  const barChartData = getBarChartData();

  return (
    <Box bg="white" style={{ minHeight: '100vh' }}>
      <Stack gap="0" px="1rem" pb="2rem" pt="1rem">
        {/* Hero Image */}
        <Card radius="md" p={0} style={{ overflow: 'hidden' }}>
          <Image src="img/setrime-energie.jpg" alt={_t('Solar panels on house')} height={200} fit="cover" />
        </Card>

        {/* Title and Description */}
        <Box>
          <H1SemiBold c={theme.colors?.gray?.[12]} mb="1.5rem" mt="1.5rem">
            {_t('Energy saving tips for your household')}
          </H1SemiBold>
          <P1Regular c={theme.colors?.gray?.[9]}>
            {_t(
              'High electricity and gas bills, rising housing costs, and a growing focus on environmental protection mean that people are looking for effective ways to save energy. Saving electricity or heat at home does not have to mean giving up comfort. Modern solutions can help a household run more comfortably, efficiently, and sustainably.'
            )}
          </P1Regular>
        </Box>

        <Box>
          <H2SemiBold c={theme.colors?.gray?.[12]} mb="1.5rem" mt="1.5rem">
            {_t('Spot prices can be more advantageous')}
          </H2SemiBold>
          <P1Regular c={theme.colors?.gray?.[9]} mb="1rem">
            {_t(
              'Electricity prices change throughout the day. In some hours, the spot price can be cheaper than a fixed price. Our calculator lets you easily check when and whether spot prices are worthwhile for you.'
            )}
          </P1Regular>
          <P1Regular c={theme.colors?.gray?.[9]}>
            {_t('That is why we prepared a calculator that shows which price is better for you.')}
            <br></br>↓
          </P1Regular>
        </Box>
      </Stack>

      {/* Calculator Widget - Full Width */}
      <Box mb="1rem">
        <Card
          style={{
            borderTop: `1px solid ${theme.colors?.gray?.[2]}`,
            borderBottom: `1px solid ${theme.colors?.gray?.[2]}`,
          }}
          bg={theme.colors?.gray?.[0]}
          radius={0}
          p="1rem"
        >
          <Box mb="1.5rem" style={{ borderBottom: `1px dashed ${theme.colors?.gray?.[4]}`, paddingBottom: '1.5rem' }}>
            <P3Bold c={theme.colors?.gray?.[10]} mb="1rem" mt="0.5rem">
              {_t('SPOT PRICE DASHBOARD')}
            </P3Bold>
            <H1SemiBold c={theme.colors?.gray?.[12]}>{_t('Spot vs fixed prices')}</H1SemiBold>
            <P1Regular c={theme.colors?.gray?.[12]} mb="1.5rem">
              {_t('Find out whether you would pay less with spot or fixed prices.')}
            </P1Regular>

            <Box>
              <P2Regular c={theme.colors?.gray?.[10]} mb="0.5rem">
                {_t('Enter your fixed price')}
              </P2Regular>
              <Flex gap="0.5rem" align="center">
                <TextInput
                  value={fixedPrice}
                  onChange={(e) => setFixedPrice(e.currentTarget.value)}
                  rightSection={
                    <Text size="16px" fw={400} c={theme.colors?.gray?.[10]} style={{ whiteSpace: 'nowrap' }}>
                      € / kWh
                    </Text>
                  }
                  rightSectionWidth={90}
                  styles={{
                    root: {
                      flex: 1,
                    },
                    input: {
                      backgroundColor: 'white',
                      border: `1px solid ${theme.colors?.gray?.[5]}`,
                      borderRadius: '8px',
                      padding: '16px 20px',
                      fontSize: '20px',
                      fontWeight: 600,
                      height: '56px',
                    },
                  }}
                />
                <Button
                  variant="filled"
                  color="violet"
                  size="lg"
                  radius="md"
                  onClick={calculateComparison}
                  disabled={loading || spotData.length === 0}
                  style={{
                    height: '56px',
                    paddingLeft: '32px',
                    paddingRight: '32px',
                    fontSize: '16px',
                    fontWeight: 600,
                  }}
                >
                  {loading ? _t('Loading...') : _t('Calculate')}
                </Button>
              </Flex>
            </Box>
          </Box>

          {/* Result */}
          <Flex gap="0.5rem" align="flex-start">
            {isSpotBetter ? (
              <Box style={{ flexShrink: 0, marginTop: '0.125rem' }}>
                <IconCheckCustom />
              </Box>
            ) : (
              <Box style={{ flexShrink: 0, marginTop: '0.125rem' }}>
                <IconXCustom />
              </Box>
            )}
            <Box>
              <H2SemiBold c={theme.colors?.gray?.[12]} mb="0.25rem">
                {isSpotBetter ? _t('Spot prices are more advantageous') : _t('Spot prices are less advantageous')}
              </H2SemiBold>
              <P1Regular c={theme.colors?.gray?.[12]}>
                <strong>
                  {isSpotBetter
                    ? savings % 1 === 0
                      ? savings
                      : savings.toFixed(1)
                    : (100 - savings) % 1 === 0
                      ? 100 - savings
                      : (100 - savings).toFixed(1)}
                  %
                </strong>{' '}
                {_t('of the time, the better option is')} {isSpotBetter ? _t('spot prices') : _t('fixed prices')}
              </P1Regular>
            </Box>
          </Flex>

          {/* Time Range Tabs */}
          <Tabs value={activeTab} onChange={(value) => setActiveTab(value as TimeRange)} mt="1.5rem" variant="pills">
            <Tabs.List
              grow
              style={{
                backgroundColor: theme.colors?.gray?.[2],
                padding: '0.25rem',
                borderRadius: '0.5rem',
              }}
            >
              <Tabs.Tab value="day" style={activeTab !== 'day' ? { backgroundColor: 'transparent' } : undefined}>
                {_t('Day')}
              </Tabs.Tab>
              <Tabs.Tab value="week" style={activeTab !== 'week' ? { backgroundColor: 'transparent' } : undefined}>
                {_t('Week')}
              </Tabs.Tab>
              <Tabs.Tab value="month" style={activeTab !== 'month' ? { backgroundColor: 'transparent' } : undefined}>
                {_t('Month')}
              </Tabs.Tab>
              <Tabs.Tab value="year" style={activeTab !== 'year' ? { backgroundColor: 'transparent' } : undefined}>
                {_t('Year')}
              </Tabs.Tab>
            </Tabs.List>
          </Tabs>

          {/* Info Box */}
          <Card bg={theme.colors?.gray?.[1]} padding="md" radius="md" withBorder mt="1rem" mb={0}>
            <Flex gap="0.5rem" align="center">
              <IconInfoCircle size="1.5rem" color={theme.colors?.gray?.[9]} style={{ flexShrink: 0 }} />
              <Text size="12px" fw={500} c={theme.colors?.gray?.[10]} style={{ lineHeight: '1.33' }}>
                {activeTab === 'day' &&
                  _t('The chart compares the spot and fixed tariff over the last 24 hours in 15-minute intervals.')}
                {activeTab === 'week' && _t('The chart compares the spot and fixed tariff over the last 7 days.')}
                {activeTab === 'month' && _t('The chart compares the spot and fixed tariff over the last 30 days.')}
                {activeTab === 'year' && _t('The chart compares the spot and fixed tariff over the last 365 days.')}
              </Text>
            </Flex>
          </Card>

          {/* Chart */}
          <Box mt="1rem" mb="1rem">
            {calculated && aggregatedData.length > 0 ? (
              <>
                {/* Chart title */}
                <Box
                  style={{
                    borderTop: `1px solid ${theme.colors?.gray?.[3]}`,
                    borderLeft: `1px solid ${theme.colors?.gray?.[3]}`,
                    borderRight: `1px solid ${theme.colors?.gray?.[3]}`,
                    borderRadius: '0.375rem 0.375rem 0 0',
                    padding: '0.5rem',
                    marginBottom: '0.25rem',
                    textAlign: 'center',
                  }}
                >
                  <Text size="12px" fw={600} c={theme.colors?.gray?.[10]} tt="uppercase">
                    {_t(getChartTitle())}
                  </Text>
                </Box>
                <Flex h={120} align="flex-end" gap="2px">
                  {barChartData.map((bar, i) => {
                    const isFirst = i === 0;
                    const isLast = i === barChartData.length - 1;
                    const hasExpensive = bar.percentageExpensive > 0;
                    const hasCheaper = bar.percentageCheaper > 0;

                    return (
                      <Flex
                        key={i}
                        direction="column"
                        style={{
                          flex: 1,
                          height: '100%',
                        }}
                      >
                        {/* Red part (expensive) - top */}
                        {hasExpensive && (
                          <Box
                            style={{
                              height: `${bar.percentageExpensive}%`,
                              backgroundColor: CHART_COLOR_EXPENSIVE,
                              borderRadius: isFirst
                                ? hasCheaper
                                  ? '0.375rem 0 0 0' // First column with both parts: only top-left
                                  : '0.375rem 0 0 0.375rem' // First column, only red: both left corners
                                : isLast
                                  ? hasCheaper
                                    ? '0 0.375rem 0 0' // Last column with both parts: only top-right
                                    : '0 0.375rem 0.375rem 0' // Last column, only red: both right corners
                                  : '0',
                            }}
                          />
                        )}
                        {/* Green part (cheaper) - bottom */}
                        {hasCheaper && (
                          <Box
                            style={{
                              height: `${bar.percentageCheaper}%`,
                              backgroundColor: CHART_COLOR_CHEAP,
                              borderRadius: isFirst
                                ? hasExpensive
                                  ? '0 0 0 0.375rem' // First column with both parts: only bottom-left
                                  : '0.375rem 0 0 0.375rem' // First column, only green: both left corners
                                : isLast
                                  ? hasExpensive
                                    ? '0 0 0.375rem 0' // Last column with both parts: only bottom-right
                                    : '0 0.375rem 0.375rem 0' // Last column, only green: both right corners
                                  : '0',
                            }}
                          />
                        )}
                      </Flex>
                    );
                  })}
                </Flex>
                {/* X-axis labels */}
                <Flex mt="0.25rem" gap="2px">
                  {xAxisLabels.map((label, i) => (
                    <Box key={i} style={{ flex: 1, textAlign: 'center' }}>
                      <Text size="10px" fw={600} c={theme.colors?.gray?.[10]} tt="uppercase">
                        {label}
                      </Text>
                    </Box>
                  ))}
                </Flex>
              </>
            ) : (
              <Flex h={120} align="center" justify="center">
                <Text size="14px" c={theme.colors?.gray?.[9]}>
                  {loading ? _t('Loading data...') : _t('Click "Calculate" to display the chart')}
                </Text>
              </Flex>
            )}
          </Box>

          {/* Legend */}
          <Flex gap="1rem">
            <Flex align="center" gap="0.25rem">
              <Box w={12} h={12} bg={CHART_COLOR_CHEAP} style={{ borderRadius: '3px' }} />
              <P3Bold c={theme.colors?.gray?.[10]}>{_t('Spot < fix')}</P3Bold>
            </Flex>
            <Flex align="center" gap="0.25rem">
              <Box w={12} h={12} bg={CHART_COLOR_EXPENSIVE} style={{ borderRadius: '3px' }} />
              <P3Bold c={theme.colors?.gray?.[10]}>{_t('spot > fix')}</P3Bold>
            </Flex>
          </Flex>
        </Card>
      </Box>

      {/* Tips Section */}
      <Stack gap="1rem" px="1rem" pb="2rem" mt="1.5rem">
        <Box>
          <H1SemiBold c={theme.colors?.gray?.[12]} mb="1.5rem">
            {_t('How to save on energy?')}
          </H1SemiBold>

          <Stack gap="1.5rem">
            <Box>
              <H2SemiBold c={theme.colors?.gray?.[12]} mb="1.5rem">
                {_t('1. Turn appliances off completely')}
              </H2SemiBold>
              <P1Regular c={theme.colors?.gray?.[12]}>
                {_t(
                  'Chargers, televisions, and computers in standby mode still consume electricity. Turning them off completely can save up to 10% of household consumption.'
                )}
              </P1Regular>
            </Box>

            <Box>
              <H2SemiBold c={theme.colors?.gray?.[12]} mb="1.5rem">
                {_t('2. Use LED lighting')}
              </H2SemiBold>
              <P1Regular c={theme.colors?.gray?.[12]}>
                {_t(
                  'LED bulbs use 80% less energy than traditional bulbs and last for several years. The investment pays back very quickly.'
                )}
              </P1Regular>
            </Box>

            <Box>
              <H2SemiBold c={theme.colors?.gray?.[12]} mb="1.5rem">
                {_t('3. Clean the refrigerator and freezer')}
              </H2SemiBold>
              <P1Regular c={theme.colors?.gray?.[12]}>
                {_t(
                  'A thick layer of frost can increase consumption by 30%. Regular defrosting and the right temperature, around 5 °C in the refrigerator, reduce bills.'
                )}
              </P1Regular>
            </Box>

            <Box>
              <H2SemiBold c={theme.colors?.gray?.[12]} mb="1.5rem">
                {_t('4. Cook with a lid')}
              </H2SemiBold>
              <P1Regular c={theme.colors?.gray?.[12]}>
                {_t(
                  'Cooking with a lid uses less energy. It is also important to choose a pot with a diameter that matches the hotplate so heat does not escape unnecessarily.'
                )}
              </P1Regular>
            </Box>

            <Box>
              <H2SemiBold c={theme.colors?.gray?.[12]} mb="1.5rem">
                {_t('5. Wash at lower temperatures')}
              </H2SemiBold>
              <P1Regular c={theme.colors?.gray?.[12]}>
                {_t(
                  'Washing at 30-40 °C is more energy efficient and wears clothes less. High temperatures are only needed for heavily soiled laundry.'
                )}
              </P1Regular>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

export default SpotComparator;
